<template>
  <el-dropdown
    v-if="eligibleTours.length > 0"
    trigger="click"
    @command="handleCommand"
    class="cc-help-menu"
    popper-class="cc-help-menu-popper"
  >
    <span class="cc-help-menu-trigger" :title="translate('Help & Tours')">
      <el-icon size="large" color="var(--main-menu-alt-text-color)"><QuestionFilled /></el-icon>
    </span>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="tour in eligibleTours"
          :key="tour.key"
          :command="tour.key"
        >
          <span class="cc-help-menu-item">
            <span class="cc-help-menu-title">{{ translate(tour.title) }}</span>
            <span
              v-if="statusFor(tour.key)"
              class="cc-help-menu-badge"
              :class="badgeClassFor(tour.key)"
            >{{ translate(statusLabelFor(tour.key)) }}</span>
          </span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script>
/* eslint-disable */
import { markRaw } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { mapState } from 'vuex'
import registry from '../definitions/_registry'

export default {
  name: 'CcHelpMenu',

  components: {
    QuestionFilled: markRaw(QuestionFilled),
  },

  computed: {
    ...mapState({
      tours: (state) => state.Tours.tours,
      progress: (state) => state.Tours.progress,
    }),

    eligibleTours() {
      // Backend already filtered by audience. We require an FE definition to exist and,
      // if that definition has a routePattern, the current path must match it (e.g. M3 is
      // only relevant on /inquiries/<id>, not on the list page).
      const currentPath = this.$route.path
      return (this.tours || []).filter(n => {
        const def = registry[n.key]
        if (!def) return false
        if (def.routePattern && !def.routePattern.test(currentPath))
          return false
        return true
      })
    },

    progressByKey() {
      const map = {}
      ;(this.progress || []).forEach(n => { map[n.tourKey] = n })
      return map
    },
  },

  methods: {
    statusFor(tourKey) {
      const row = this.progressByKey[tourKey]
      return row ? row.status : null
    },

    statusLabelFor(tourKey) {
      const status = this.statusFor(tourKey)
      switch (status) {
        case 'InProgress': return 'In progress'
        case 'Completed': return 'Completed'
        case 'Skipped': return 'Skipped'
        default: return ''
      }
    },

    badgeClassFor(tourKey) {
      const status = this.statusFor(tourKey)
      switch (status) {
        case 'InProgress': return 'cc-badge--in-progress'
        case 'Completed': return 'cc-badge--completed'
        case 'Skipped': return 'cc-badge--skipped'
        default: return ''
      }
    },

    async handleCommand(tourKey) {
      const def = registry[tourKey]
      if (!def) return

      const status = this.statusFor(tourKey)

      const navigateThenStart = async (action) => {
        try {
          if (def.route && this.$route.path !== def.route) {
            await this.$router.push(def.route)
          }
          await this.$store.dispatch(`Tours/${action}`, tourKey)
        } catch (err) {
          this.showApiError(err)
        }
      }

      if (status === 'Completed' || status === 'Skipped') {
        this.confirmExecute('Reset this tour to take it again?', async () => {
          try {
            await this.$store.dispatch('Tours/resetTour', tourKey)
            await navigateThenStart('startTour')
          } catch (err) {
            this.showApiError(err)
          }
        })
        return
      }

      if (status === 'InProgress') {
        await navigateThenStart('resumeTour')
        return
      }

      await navigateThenStart('startTour')
    },
  },
}
</script>

<style scoped>
.cc-help-menu {
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
  cursor: pointer;
}

.cc-help-menu-trigger {
  display: inline-flex;
  align-items: center;
}

.cc-help-menu-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.cc-help-menu-badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  border-radius: 8px;
  color: #fff;
  line-height: 14px;
}

.cc-badge--in-progress {
  background-color: #e6a23c;
}

.cc-badge--completed {
  background-color: #67c23a;
}

.cc-badge--skipped {
  background-color: #909399;
}
</style>

<style>
/* Unscoped: the popper is teleported to <body>, so scoped styles can't reach it.
   The navbar uses z-index:99999 (prodexis.css), well above Element Plus's default 2000+
   stack, so the popper renders underneath it without this override.

   Colors use the appSkin CSS vars (--background-color, --main-text-color) so the
   dropdown adapts to both default and tech-dark skins. Without explicit color rules
   the item text inherits a value that blends into the popper background in dark mode
   (visible only on hover via Element Plus's hover style). */
.cc-help-menu-popper {
  z-index: 100000 !important;
  background-color: var(--background-color, #ffffff) !important;
  /* Match .el-dialog / .el-notification / .swal2-popup border in prodexis.css. */
  border: 1px solid var(--control-border-color, #dcdfe6) !important;
  border-radius: 4px !important;
}

.cc-help-menu-popper .el-popper__arrow::before {
  border: 1px solid var(--control-border-color, #dcdfe6) !important;
}

.cc-help-menu-popper .el-dropdown-menu,
.cc-help-menu-popper .el-popper__arrow::before {
  background-color: var(--background-color, #ffffff) !important;
}

.cc-help-menu-popper .el-dropdown-menu__item {
  color: var(--main-text-color, #303133) !important;
}

.cc-help-menu-popper .el-dropdown-menu__item:not(.is-disabled):hover,
.cc-help-menu-popper .el-dropdown-menu__item:focus {
  background-color: var(--alt-background-color, #f5f7fa) !important;
  color: var(--main-menu-active-text-color, #409eff) !important;
}

.cc-help-menu-popper .cc-help-menu-title {
  color: inherit;
}
</style>
